import { parseMultiSelectOptions } from '../../SectionListCrud/editCells/multiSelectCellUtils';

/** Application metadata on colDefs — not valid AG Grid props (use colDef.context). */
export const COL_DEF_CONTEXT_KEYS = [
    'crudType',
    'fieldSelectorLabel',
    'fieldConfiguration',
    'suppressFromFieldSelector',
    'included',
    'sortField',
    'permission',
    'hidden',
    'forceExport',
    'showInExport',
];

const OBJECT_CELL_RENDERERS = new Set(['MultiSelectReadCell', 'MultiSelectCell']);

export function getColDefContextValue(colDef, key) {
    if (!colDef) return undefined;
    if (colDef.context && key in colDef.context) return colDef.context[key];
    return colDef[key];
}

export function getHeaderSortField(params) {
    if (!params) return undefined;
    if (params.sortField) return params.sortField;

    const colDef = params.column?.colDef;
    if (!colDef) return undefined;

    return getColDefContextValue(colDef, 'sortField');
}

export function isHeaderSortingEnabled(params) {
    if (params?.enableSorting != null) return params.enableSorting;
    if (params?.column?.getColDef) return params.column.getColDef()?.sortable !== false;
    return params?.column?.colDef?.sortable !== false;
}

export function formatObjectCellValue(value) {
    if (value == null || value === '') return '';
    if (typeof value !== 'object') return String(value);

    const options = parseMultiSelectOptions(value);
    if (options.length) {
        return options.map((option) => option.label).join(', ');
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (item != null && typeof item === 'object') {
                    return item.description ?? item.label ?? item.value ?? '';
                }
                return item == null ? '' : String(item);
            })
            .filter(Boolean)
            .join(', ');
    }

    return value.description ?? value.label ?? value.value ?? '';
}

export function sanitizeColDefForAgGrid(colDef) {
    if (!colDef) return colDef;

    const sanitized = { ...colDef };
    const context = { ...colDef.context };

    COL_DEF_CONTEXT_KEYS.forEach((key) => {
        if (key in colDef) {
            context[key] = colDef[key];
            delete sanitized[key];
        }
    });

    if (Object.keys(context).length > 0) {
        sanitized.context = context;
    }

    const rendererName = typeof sanitized.cellRenderer === 'string' ? sanitized.cellRenderer : null;

    if (!sanitized.valueFormatter && OBJECT_CELL_RENDERERS.has(rendererName)) {
        sanitized.cellDataType = false;
        sanitized.valueFormatter = (params) => formatObjectCellValue(params.value);
    }

    return sanitized;
}

export function toAgGridRowId(id) {
    if (id == null || id === '') return '';
    return String(id);
}

const autoAgGridRowIds = new WeakMap();
let autoAgGridRowIdSequence = 0;

/** Stable row id for AG Grid when row data has no entity id (id/Id/ID). */
export function resolveDefaultAgGridRowId(data) {
    if (data == null) return '';

    const entityId = data.id ?? data.Id ?? data.ID;
    if (entityId != null && entityId !== '') {
        return toAgGridRowId(entityId);
    }

    let autoId = autoAgGridRowIds.get(data);
    if (!autoId) {
        autoId = `w5-auto-row-${++autoAgGridRowIdSequence}`;
        autoAgGridRowIds.set(data, autoId);
    }

    return autoId;
}

/** AG Grid v32+: hideOverlay does not clear the loading overlay while loading=true. */
export function hideGridOverlay(gridApi) {
    if (!gridApi) return;
    gridApi.setGridOption?.('loading', false);
    gridApi.hideOverlay?.();
}

/** Application metadata on gridOptions — not valid AG Grid props (use gridOptions.context). */
const GRID_OPTIONS_CONTEXT_KEYS = ['layoutInterval'];

/** DataGrid-only props that must not be forwarded to AG Grid. */
const GRID_OPTIONS_STRIP_KEYS = [
    'customButtonLiteral',
    'useLoaderFullHeight',
    'useLoaderPlaceholder',
    'useLoaderPlaceholderFullHeight',
    'loaderPlaceholderRowHeight',
    'loaderPlaceholderRows',
    'loaderPlaceholderColumns',
    'placeholderOptions',
];

const NUMERIC_GRID_OPTION_KEYS = [
    'headerHeight',
    'rowHeight',
    'floatingFiltersHeight',
    'groupHeaderHeight',
    'pivotHeaderHeight',
    'detailRowHeight',
];

export function coerceNumericGridOption(value) {
    if (value == null || value === '') return value;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : value;
}

const LEGACY_ROW_SELECTION_MODES = {
    single: 'singleRow',
    multiple: 'multiRow',
};

export function normalizeRowSelection(rowSelection, suppressRowClickSelection) {
    let normalized;

    if (typeof rowSelection === 'string') {
        normalized = {
            mode: LEGACY_ROW_SELECTION_MODES[rowSelection] ?? 'singleRow',
        };
    } else if (rowSelection && typeof rowSelection === 'object') {
        normalized = { ...rowSelection };
        if (typeof normalized.mode === 'string' && LEGACY_ROW_SELECTION_MODES[normalized.mode]) {
            normalized.mode = LEGACY_ROW_SELECTION_MODES[normalized.mode];
        }
    } else {
        normalized = { mode: 'singleRow' };
    }

    if (suppressRowClickSelection === true) {
        normalized.enableClickSelection = false;
    }

    // AG Grid v32.2+ defaults checkboxes/headerCheckbox to true, which adds a dedicated
    // selection column. DataGrid uses custom checkboxes inside column renderers instead.
    if (normalized.checkboxes === undefined) {
        normalized.checkboxes = false;
    }
    if (normalized.headerCheckbox === undefined) {
        normalized.headerCheckbox = false;
    }

    return normalized;
}

export function sanitizeGridOptionsForAgGrid(gridOptions) {
    if (!gridOptions) {
        return { gridOptions: {}, defaultColDefOverrides: {} };
    }

    const sanitized = { ...gridOptions };
    const defaultColDefOverrides = {};
    const context = { ...sanitized.context };
    const suppressRowClickSelection = sanitized.suppressRowClickSelection;

    delete sanitized.suppressRowClickSelection;

    if (sanitized.rowSelection !== undefined || suppressRowClickSelection !== undefined) {
        sanitized.rowSelection = normalizeRowSelection(
            sanitized.rowSelection,
            suppressRowClickSelection,
        );
    }

    if (sanitized.suppressKeyboardEvent) {
        defaultColDefOverrides.suppressKeyboardEvent = sanitized.suppressKeyboardEvent;
        delete sanitized.suppressKeyboardEvent;
    }

    GRID_OPTIONS_CONTEXT_KEYS.forEach((key) => {
        if (key in sanitized) {
            context[key] = sanitized[key];
            delete sanitized[key];
        }
    });

    GRID_OPTIONS_STRIP_KEYS.forEach((key) => {
        delete sanitized[key];
    });

    NUMERIC_GRID_OPTION_KEYS.forEach((key) => {
        if (key in sanitized) {
            sanitized[key] = coerceNumericGridOption(sanitized[key]);
        }
    });

    if (Object.keys(context).length > 0) {
        sanitized.context = context;
    }

    return { gridOptions: sanitized, defaultColDefOverrides };
}

/** AG Grid v31+ auto-unpins columns when pinned width exceeds viewport − 50px. Return none. */
export function preventAutoUnpinColumnsOnResize() {
    return [];
}

export function getAgGridColumnId(column) {
    if (!column) return null;
    return column.getColId?.() ?? column.colId ?? null;
}

export function isRowDragEnabled(params) {
    const rowDrag = params?.colDef?.rowDrag;
    if (!rowDrag) return false;
    if (rowDrag === true) return true;
    if (typeof rowDrag === 'function') {
        try {
            return Boolean(rowDrag(params));
        } catch {
            return false;
        }
    }
    return false;
}

export function getAgGridColumnWidth(column) {
    if (!column) return null;
    const width = column.getActualWidth?.() ?? column.actualWidth;
    return width != null ? width : null;
}

export function getResizedColumnsFromEvent(event) {
    if (!event) return [];
    if (event.columns?.length) {
        const flexColumns = new Set(event.flexColumns ?? []);
        const resizedColumns = event.columns.filter((column) => !flexColumns.has(column));

        return resizedColumns.length ? resizedColumns : event.column ? [event.column] : [];
    }
    if (event.column) return [event.column];
    return [];
}

export const FIELD_SELECTOR_SPACER_COL_ID = 'fmFieldSelectorSpacer';
/** Width reserved in the header for the field-selector menu. */
export const FIELD_SELECTOR_SPACER_WIDTH = 44;

/** Trailing center column reserves header width; menu is the absolute overlay. */
export function getFieldSelectorSpacerColDef({ fill = false } = {}) {
    return {
        colId: FIELD_SELECTOR_SPACER_COL_ID,
        lockPosition: 'right',
        suppressMovable: true,
        ...(fill
            ? {
                  cellClass: 'w5-cell__field-selector--fill',
                  flex: 1,
                  minWidth: 0,
                  suppressSizeToFit: false,
              }
            : {
                  cellClass: 'w5-cell__field-selector',
                  maxWidth: FIELD_SELECTOR_SPACER_WIDTH,
                  minWidth: FIELD_SELECTOR_SPACER_WIDTH,
                  suppressSizeToFit: true,
                  width: FIELD_SELECTOR_SPACER_WIDTH,
              }),
        sortable: false,
        filter: false,
        resizable: false,
        headerComponent: 'headerBlankCell',
        headerClass: 'w5-header w5-header__field-selector',
        hide: false,
    };
}

/** Keep spacer width and last data column resizable after grid layout changes. */
export function applyFieldSelectorSpacerColumnState(gridApi, columnDefs) {
    if (!gridApi) return;

    const defs = gridApi.getColumnDefs?.() ?? columnDefs ?? [];
    const hasVisibleSelectorColumn = hasVisibleFieldSelectorColumn(defs);
    const state = [
        {
            colId: FIELD_SELECTOR_SPACER_COL_ID,
            pinned: null,
            hide: false,
            ...(hasVisibleSelectorColumn
                ? { flex: null, width: FIELD_SELECTOR_SPACER_WIDTH }
                : { flex: 1 }),
        },
    ];

    const lastDataIdx = getLastVisibleDataColumnIndex(defs);
    const lastCol = lastDataIdx >= 0 ? defs[lastDataIdx] : null;
    const lastColId = lastCol?.colId || lastCol?.field;
    if (lastColId && lastCol?.resizable !== false) {
        state.push({ colId: lastColId, resizable: true });
    }

    gridApi.applyColumnState?.({
        state,
        applyOrder: false,
    });
}

export function getLastDataColumnIndex(columnDefs) {
    if (!columnDefs?.length) return -1;
    for (let i = columnDefs.length - 1; i >= 0; i--) {
        const colId = columnDefs[i]?.colId;
        if (colId && colId !== FIELD_SELECTOR_SPACER_COL_ID) return i;
    }
    return -1;
}

function isHiddenDataGridColumn(colDef) {
    return Boolean(colDef?.hide);
}

export function hasVisibleFieldSelectorColumn(columnDefs, getHide = isHiddenDataGridColumn) {
    if (!columnDefs?.length) return false;

    return columnDefs.some((col) => {
        const colId = col?.colId || col?.field;
        if (!colId || colId === FIELD_SELECTOR_SPACER_COL_ID) return false;
        if (getColDefContextValue(col, 'suppressFromFieldSelector')) return false;
        return !getHide(col);
    });
}

export function getLastVisibleDataColumnIndex(columnDefs) {
    if (!columnDefs?.length) return -1;
    for (let i = columnDefs.length - 1; i >= 0; i--) {
        const col = columnDefs[i];
        const colId = col?.colId || col?.field;
        if (!colId || colId === FIELD_SELECTOR_SPACER_COL_ID) continue;
        if (isHiddenDataGridColumn(col)) continue;
        return i;
    }
    return -1;
}

/** True when the consumer (or cache) set an explicit width on any visible data column. */
export function hasExplicitColumnWidthsFromDefs(columnDefs, options = {}) {
    const { excludeColIds = [FIELD_SELECTOR_SPACER_COL_ID] } = options;
    if (!columnDefs?.length) return false;

    return columnDefs.some((col) => {
        const colId = col.colId || col.field;
        if (!colId || excludeColIds.includes(colId)) return false;
        if (isHiddenDataGridColumn(col)) return false;
        if (col.flex) return false;
        return col.width != null;
    });
}

export function applyUserColumnWidthToGridColDef(gridApi, colId, width, options = {}) {
    const { preserveFlexLastDataColumn = false } = options;
    if (!gridApi || !colId || width == null) return false;

    const defs = gridApi.getColumnDefs?.() ?? [];
    const def = defs.find((item) => (item.colId || item.field) === colId);
    if (!def) return false;

    const defIdx = defs.indexOf(def);
    const lastDataIdx = getLastVisibleDataColumnIndex(defs);
    if (preserveFlexLastDataColumn && defIdx === lastDataIdx && defIdx >= 0 && def.flex) {
        return false;
    }

    def.width = width;
    def.suppressSizeToFit = true;
    if (def.flex != null) delete def.flex;

    return true;
}

export function syncUserColumnWidthsToGridColDefs(
    gridApi,
    widthsByColId,
    resizedColumnIds,
    options = {},
) {
    if (!gridApi || !widthsByColId || !resizedColumnIds?.size) return;

    const defs = gridApi.getColumnDefs?.() ?? [];
    const lastDataIdx = getLastVisibleDataColumnIndex(defs);
    const { preserveFlexLastDataColumn = false } = options;

    resizedColumnIds.forEach((colId) => {
        const width = widthsByColId[colId];
        if (width == null) return;

        const def = defs.find((item) => (item.colId || item.field) === colId);
        if (!def) return;

        const defIdx = defs.indexOf(def);
        if (preserveFlexLastDataColumn && defIdx === lastDataIdx && defIdx >= 0 && def.flex) {
            return;
        }

        def.width = width;
        def.suppressSizeToFit = true;
        if (def.flex != null) delete def.flex;
    });
}

/** Max leaf columns before AG Grid column virtualisation stays enabled. */
export const SUPPRESS_COLUMN_VIRTUALISATION_MAX_COLS = 25;

/**
 * Counts renderable leaf columns (group headers with children are expanded).
 * @param {import('ag-grid-community').ColDef[]} [columnDefs]
 * @returns {number}
 */
export function countLeafColumnDefs(columnDefs) {
    if (!columnDefs?.length) return 0;

    return columnDefs.reduce((count, col) => {
        if (col.children?.length) {
            return count + countLeafColumnDefs(col.children);
        }
        if (col.hide === true || getColDefContextValue(col, 'hidden') === true) {
            return count;
        }
        return count + 1;
    }, 0);
}

/**
 * Disables column virtualisation for moderately wide grids so horizontal scroll does not
 * recreate DOM-heavy cells for every visible row on each column enter/exit.
 * @param {import('ag-grid-community').ColDef[]} [columnDefs]
 * @param {number} [maxCols]
 * @returns {boolean}
 */
export function shouldSuppressColumnVirtualisation(
    columnDefs,
    maxCols = SUPPRESS_COLUMN_VIRTUALISATION_MAX_COLS,
) {
    const count = countLeafColumnDefs(columnDefs);
    return count > 0 && count <= maxCols;
}
